import { supabase } from '../utils/supabase';
import { getCurrentSeasonId } from '../utils/seasonUtils';
import {
  fetchViewerSessionBrowseContext,
  canViewSessionInBrowseWithContext,
} from '../utils/sessionVisibilityUtils';
import { triggerSessionCompletionEffects } from './sessionCompletionService';
import { normalizePegBoardQueueReturn } from '../utils/pegBoard/constants';
import { canSignupByCapacity, initialSignupPlacement, isHostSelectSignup } from '../utils/signupSelection';

/**
 * Session Management Service
 * Handles session creation, signup, and management functionality
 */

// Create a new session from a template
export const createSession = async (sessionData) => {
  try {
    const {
      club_id,
      session_date,
      start_time,
      name,
      description,
      max_players,
      waitlist_enabled = true,
      waitlist_promotion_type = 'first_come_first_served',
      signup_selection = 'first_come',
      signup_deadline_hours = null,
      created_by,
      is_public = false,
      session_type_id = null,
      season_id = null,
      // New template fields
      number_of_courts = null,
      price_per_player = null,
      court_booking_cost = null,
      location = null,
      min_elo_level = null,
      currency_code = 'GBP',
      end_time = null,
      game_type_restriction = 'all_types',
      enable_player_categorisation = false,
      competitive_match_format = 'doubles',
      points_target = 21,
      deuce_mode = 'standard',
      peg_board_selection_window = 6,
      peg_board_queue_return = undefined,
      session_series_id = null,
    } = sessionData;

    // Get season_id if not provided
    let finalSeasonId = season_id;
    if (!finalSeasonId) {
      console.log('No season_id provided, fetching current season for club:', club_id);
      try {
        finalSeasonId = await getCurrentSeasonId(club_id);
        console.log('Using season_id:', finalSeasonId);
      } catch (seasonError) {
        console.error('Error getting season ID:', seasonError);
        throw new Error('Could not determine season for session');
      }
    }

    // With new database structure, session_date is DATE and start_time/end_time are TIME
    // No need to combine them - they're stored separately
    if (!session_date || !start_time) {
      throw new Error('Session date and start time are required');
    }

    const { data, error } = await supabase
      .from('sessions')
      .insert([{
        club_id,
        session_date: session_date, // Now a DATE field
        start_time: start_time,      // Now a TIME field
        name,
        description,
        max_players,
        waitlist_enabled: true,
        waitlist_promotion_type,
        signup_selection: isHostSelectSignup(signup_selection) ? 'host_select' : 'first_come',
        signup_deadline_hours,
        created_by,
        is_public,
        session_type_id,
        season_id: finalSeasonId,
        session_status: sessionData.session_status || 'draft',
        // New template fields
        number_of_courts,
        price_per_player,
        court_booking_cost,
        location,
        min_elo_level,
        currency_code,
        end_time: end_time || null, // Now a TIME field
        game_type_restriction,
        enable_player_categorisation,
        competitive_match_format,
        points_target,
        deuce_mode,
        peg_board_selection_window,
        peg_board_queue_return: normalizePegBoardQueueReturn(peg_board_queue_return),
        session_series_id: session_series_id || null,
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating session:', error);
    throw error;
  }
};

// Get upcoming sessions for a club
// Get all public sessions from all clubs
export const getPublicSessions = async (filters = {}) => {
  try {
    console.log('Fetching public sessions with filters:', filters);
    
    let query = supabase
      .from('sessions')
      .select(`
        *,
        session_types:session_type_id (
          id,
          name,
          description
        ),
        clubs:club_id (
          id,
          name,
          location,
          banner_image_url,
          logo_image_url
        )
      `)
      .eq('is_public', true)
      .neq('session_status', 'draft')
      .neq('session_status', 'completed')  // Exclude completed sessions
      .neq('session_status', 'archived')   // Exclude archived sessions
      .gte('session_date', new Date().toISOString().split('T')[0])
      .order('session_date', { ascending: true });

    // Apply filters
    if (filters.excludeCancelled) {
      query = query.neq('session_status', 'cancelled');
    }

    if (filters.session_type_id) {
      query = query.eq('session_type_id', filters.session_type_id);
    }

    // Apply date range filters
    if (filters.date_range) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      switch (filters.date_range) {
        case 'today':
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          query = query.gte('session_date', today.toISOString())
                      .lt('session_date', tomorrow.toISOString());
          break;
        case 'tomorrow':
          const tomorrowStart = new Date(today);
          tomorrowStart.setDate(tomorrowStart.getDate() + 1);
          const dayAfterTomorrow = new Date(today);
          dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
          query = query.gte('session_date', tomorrowStart.toISOString())
                      .lt('session_date', dayAfterTomorrow.toISOString());
          break;
        case 'this_week':
          const weekStart = new Date(today);
          weekStart.setDate(weekStart.getDate() - weekStart.getDay());
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekEnd.getDate() + 7);
          query = query.gte('session_date', weekStart.toISOString())
                      .lt('session_date', weekEnd.toISOString());
          break;
        case 'next_week':
          const nextWeekStart = new Date(today);
          nextWeekStart.setDate(nextWeekStart.getDate() + (7 - nextWeekStart.getDay()));
          const nextWeekEnd = new Date(nextWeekStart);
          nextWeekEnd.setDate(nextWeekEnd.getDate() + 7);
          query = query.gte('session_date', nextWeekStart.toISOString())
                      .lt('session_date', nextWeekEnd.toISOString());
          break;
        case 'this_month':
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
          query = query.gte('session_date', monthStart.toISOString())
                      .lt('session_date', monthEnd.toISOString());
          break;
      }
    }

    const { data: basicData, error: basicError } = await query;
    
    if (basicError) {
      console.error('Supabase error on public sessions query:', basicError);
      throw basicError;
    }

    console.log('Public sessions data:', basicData);

    // Process sessions to include signup data
    const processedSessions = await Promise.all(basicData.map(async (session) => {
      try {
        // Get signup counts for this session
        const { data: signupsData, error: signupsError } = await supabase
          .from('session_signups')
          .select('id, signup_status, waitlist_position')
          .eq('session_id', session.id);

        let confirmedCount = 0;
        let waitlistCount = 0;

        if (!signupsError && signupsData) {
          confirmedCount = signupsData.filter(s => s.signup_status === 'confirmed').length;
          waitlistCount = signupsData.filter(s => s.signup_status === 'waitlist').length;
        }

        return {
          ...session,
          current_players: session.current_players || confirmedCount,
          confirmed_players: confirmedCount,
          waitlist_players: waitlistCount,
          is_full: confirmedCount >= (session.max_players || 16),
          can_signup: canSignupByCapacity(session, confirmedCount),
          is_public: true // Ensure this is set for public sessions
        };
      } catch (err) {
        console.error('Error processing public session:', session.id, err);
        return {
          ...session,
          current_players: 0,
          confirmed_players: 0,
          waitlist_players: 0,
          is_full: false,
          can_signup: true,
          is_public: true
        };
      }
    }));

    // Apply availability filter after processing
    let filteredSessions = processedSessions;
    if (filters.availability) {
      filteredSessions = processedSessions.filter(session => {
        switch (filters.availability) {
          case 'open':
            return session.can_signup && (!session.is_full || isHostSelectSignup(session));
          case 'waitlist':
            return isHostSelectSignup(session)
              ? session.waitlist_players > 0
              : session.is_full && session.waitlist_enabled && session.waitlist_players > 0;
          case 'full':
            return !isHostSelectSignup(session) && session.is_full && (!session.waitlist_enabled || session.waitlist_players === 0);
          default:
            return true;
        }
      });
    }

    return filteredSessions;
  } catch (error) {
    console.error('Error fetching public sessions:', error);
    throw error;
  }
};

/**
 * Get all sessions viewable when no club is selected: sessions from every club.
 * Uses the clubs list so the user sees the same sessions as when browsing by club.
 * Respects visibility filter (all / public / private).
 */
export const getAllViewableSessions = async (userId, filters = {}) => {
  try {
    const [{ data: clubsData, error: clubsError }, browseContext] = await Promise.all([
      supabase.from('clubs').select('id'),
      fetchViewerSessionBrowseContext(userId),
    ]);

    if (clubsError) throw clubsError;
    const clubIds = (clubsData || []).map((c) => c.id);
    if (clubIds.length === 0) return [];

    const clubSessionsArrays = await Promise.all(
      clubIds.map((clubId) => getUpcomingSessions(clubId, filters))
    );

    const byId = new Map();
    for (const arr of clubSessionsArrays) {
      for (const s of arr) {
        byId.set(s.id, s);
      }
    }

    const merged = Array.from(byId.values())
      .filter((session) => canViewSessionInBrowseWithContext(session, browseContext))
      .sort((a, b) => {
        const d = (a.session_date || '').localeCompare(b.session_date || '');
        if (d !== 0) return d;
        return (a.start_time || '').localeCompare(b.start_time || '');
      });

    return merged;
  } catch (error) {
    console.error('Error fetching all viewable sessions:', error);
    throw error;
  }
};

export const fetchSessionSignupCountsMap = async (sessionIds) => {
  const map = new Map();
  const ids = [...new Set((sessionIds || []).filter(Boolean))];
  if (!ids.length) return map;

  const { data, error } = await supabase
    .from('session_signups')
    .select('session_id, signup_status')
    .in('session_id', ids)
    .in('signup_status', ['confirmed', 'waitlist']);

  if (error) throw error;

  (data || []).forEach((row) => {
    if (!map.has(row.session_id)) {
      map.set(row.session_id, { confirmed: 0, waitlist: 0 });
    }
    const counts = map.get(row.session_id);
    if (row.signup_status === 'confirmed') counts.confirmed += 1;
    if (row.signup_status === 'waitlist') counts.waitlist += 1;
  });

  return map;
};

export const mergeSessionSignupCounts = (sessions, countMap) =>
  (sessions || []).map((session) => {
    const counts = countMap?.get(session.id);
    if (!counts) {
      return {
        ...session,
        confirmed_count: session.confirmed_count ?? session.current_players ?? 0,
        waitlist_count: session.waitlist_count ?? 0,
      };
    }
    return {
      ...session,
      confirmed_count: counts.confirmed,
      waitlist_count: counts.waitlist,
      current_players: counts.confirmed,
    };
  });

// Get all sessions for admin management (including drafts)
export const getAllSessions = async (clubId, filters = {}) => {
  try {
    console.log('Fetching all sessions for club:', clubId, 'with filters:', filters);
    
    let query = supabase
      .from('sessions')
      .select(`
        *,
        session_types:session_type_id (
          id,
          name,
          description
        ),
        session_series:session_series_id (
          id,
          name,
          is_active
        )
      `)
      .eq('club_id', clubId)
      .not('session_date', 'is', null)  // Filter out sessions with null dates
      .gte('session_date', new Date().toISOString().split('T')[0])
      .neq('session_status', 'completed')  // Exclude completed sessions
      .neq('session_status', 'archived')   // Exclude archived sessions
      .order('session_date', { ascending: true });

    // Apply filters
    if (filters.excludeCancelled) {
      query = query.neq('session_status', 'cancelled');
    }

    if (filters.session_type_id) {
      query = query.eq('session_type_id', filters.session_type_id);
    }

    // Apply date range filters
    if (filters.date_range) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      switch (filters.date_range) {
        case 'today':
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          query = query.gte('session_date', today.toISOString())
                      .lt('session_date', tomorrow.toISOString());
          break;
        case 'tomorrow':
          const tomorrowStart = new Date(today);
          tomorrowStart.setDate(tomorrowStart.getDate() + 1);
          const dayAfterTomorrow = new Date(tomorrowStart);
          dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 1);
          query = query.gte('session_date', tomorrowStart.toISOString())
                      .lt('session_date', dayAfterTomorrow.toISOString());
          break;
        case 'this_week':
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 7);
          query = query.gte('session_date', weekStart.toISOString())
                      .lt('session_date', weekEnd.toISOString());
          break;
        case 'next_week':
          const nextWeekStart = new Date(today);
          nextWeekStart.setDate(today.getDate() + (7 - today.getDay())); // Start of next week
          const nextWeekEnd = new Date(nextWeekStart);
          nextWeekEnd.setDate(nextWeekStart.getDate() + 7);
          query = query.gte('session_date', nextWeekStart.toISOString())
                      .lt('session_date', nextWeekEnd.toISOString());
          break;
      }
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching all sessions:', error);
      throw error;
    }

    console.log('All sessions fetched:', data?.length || 0);
    return data || [];
  } catch (error) {
    console.error('Error in getAllSessions:', error);
    throw error;
  }
};

// Get upcoming sessions for public display (only live sessions)
export const getUpcomingSessions = async (clubId, filters = {}) => {
  try {
    console.log('Fetching upcoming sessions for club:', clubId, 'with filters:', filters);
    
    // First, let's try a simple query to see what's available
    let query = supabase
      .from('sessions')
      .select(`
        *,
        session_types:session_type_id (
          id,
          name,
          description
        ),
        clubs:club_id (
          id,
          name,
          location,
          banner_image_url,
          logo_image_url
        )
      `)
      .eq('club_id', clubId)
      .neq('session_status', 'draft')
      .neq('session_status', 'completed')  // Exclude completed sessions
      .neq('session_status', 'archived')   // Exclude archived sessions
      .not('session_date', 'is', null)  // Filter out sessions with null dates
      .gte('session_date', new Date().toISOString().split('T')[0])
      .order('session_date', { ascending: true });

    // Apply filters
    if (filters.excludeCancelled) {
      query = query.neq('session_status', 'cancelled');
    }

    if (filters.session_type_id) {
      query = query.eq('session_type_id', filters.session_type_id);
    }

    // Apply visibility filter (public/private)
    if (filters.visibility) {
      if (filters.visibility === 'public') {
        query = query.eq('is_public', true);
      } else if (filters.visibility === 'private') {
        query = query.eq('is_public', false);
      }
    }

    // Apply date range filters
    if (filters.date_range) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      switch (filters.date_range) {
        case 'today':
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          query = query.gte('session_date', today.toISOString())
                      .lt('session_date', tomorrow.toISOString());
          break;
        case 'tomorrow':
          const tomorrowStart = new Date(today);
          tomorrowStart.setDate(tomorrowStart.getDate() + 1);
          const dayAfterTomorrow = new Date(today);
          dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
          query = query.gte('session_date', tomorrowStart.toISOString())
                      .lt('session_date', dayAfterTomorrow.toISOString());
          break;
        case 'this_week':
          const weekStart = new Date(today);
          weekStart.setDate(weekStart.getDate() - weekStart.getDay());
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekEnd.getDate() + 7);
          query = query.gte('session_date', weekStart.toISOString())
                      .lt('session_date', weekEnd.toISOString());
          break;
        case 'next_week':
          const nextWeekStart = new Date(today);
          nextWeekStart.setDate(nextWeekStart.getDate() + (7 - nextWeekStart.getDay()));
          const nextWeekEnd = new Date(nextWeekStart);
          nextWeekEnd.setDate(nextWeekEnd.getDate() + 7);
          query = query.gte('session_date', nextWeekStart.toISOString())
                      .lt('session_date', nextWeekEnd.toISOString());
          break;
        case 'this_month':
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
          query = query.gte('session_date', monthStart.toISOString())
                      .lt('session_date', monthEnd.toISOString());
          break;
      }
    }

    const { data: basicData, error: basicError } = await query;
    
    if (basicError) {
      console.error('Supabase error on basic query:', basicError);
      throw basicError;
    }

    console.log('Basic sessions data:', basicData);

    // For now, let's fetch signup data separately to avoid relationship issues
    const processedSessions = await Promise.all(basicData.map(async (session) => {
      try {
        // Get signup counts for this session
        const { data: signupsData, error: signupsError } = await supabase
          .from('session_signups')
          .select('id, signup_status, waitlist_position')
          .eq('session_id', session.id);

        let confirmedCount = 0;
        let waitlistCount = 0;

        if (!signupsError && signupsData) {
          confirmedCount = signupsData.filter(s => s.signup_status === 'confirmed').length;
          waitlistCount = signupsData.filter(s => s.signup_status === 'waitlist').length;
        }

        return {
          ...session,
          current_players: session.current_players || confirmedCount,
          confirmed_players: confirmedCount,
          waitlist_players: waitlistCount,
          is_full: confirmedCount >= (session.max_players || 16),
          can_signup: canSignupByCapacity(session, confirmedCount),
          session_signups: signupsData || []
        };
      } catch (err) {
        console.error('Error fetching signups for session:', session.id, err);
        return {
          ...session,
          current_players: session.current_players || 0,
          confirmed_players: 0,
          waitlist_players: 0,
          is_full: false,
          can_signup: true,
          session_signups: []
        };
      }
    }));

    console.log('Processed sessions:', processedSessions);

    // Apply availability filter after processing
    let filteredSessions = processedSessions;
    if (filters.availability) {
      filteredSessions = processedSessions.filter(session => {
        switch (filters.availability) {
          case 'open':
            return session.can_signup && (!session.is_full || isHostSelectSignup(session));
          case 'waitlist':
            return isHostSelectSignup(session)
              ? session.waitlist_players > 0
              : session.is_full && session.waitlist_enabled && session.waitlist_players > 0;
          case 'full':
            return !isHostSelectSignup(session) && session.is_full && (!session.waitlist_enabled || session.waitlist_players === 0);
          default:
            return true;
        }
      });
    }

    return filteredSessions;
  } catch (error) {
    console.error('Error fetching upcoming sessions:', error);
    console.error('Full error object:', JSON.stringify(error, null, 2));
    throw error;
  }
};

// Get a single session with full details
export const getSessionDetails = async (sessionId) => {
  try {
    // Get basic session data
    const { data: sessionData, error: sessionError } = await supabase
      .from('sessions')
      .select('*')
      .eq('id', sessionId)
      .single();

    if (sessionError) throw sessionError;
    
    console.log('[SessionDetails] Raw session data:', sessionData);

    // Get session signups with player details
    const { data: signupsData, error: signupsError } = await supabase
      .from('session_signups')
      .select(`
        id,
        player_id,
        signup_status,
        waitlist_position,
        signup_date,
        added_by,
        players!session_signups_player_id_fkey(first_name, last_name, email, is_guest, gender)
      `)
      .eq('session_id', sessionId)
      .order('signup_date', { ascending: true });

    if (signupsError) {
      console.error('Error fetching signups:', signupsError);
      // Continue without signups if there's an error
    }

    // Get club information
    const { data: clubData, error: clubError } = await supabase
      .from('clubs')
      .select('name, location, logo_image_url')
      .eq('id', sessionData.club_id)
      .single();

    if (clubError) {
      console.error('Error fetching club:', clubError);
      // Continue without club data if there's an error
    }

    // Get creator information (only if created_by is not null)
    let creatorData = null;
    if (sessionData.created_by) {
      const { data, error: creatorError } = await supabase
        .from('players')
        .select('first_name, last_name, email')
        .eq('id', sessionData.created_by)
        .single();

      if (creatorError) {
        console.error('Error fetching creator:', creatorError);
        // Continue without creator data if there's an error
      } else {
        creatorData = data;
      }
    }

    // Session setup information is no longer needed as session_setup_id has been removed
    let setupData = null;

    // Process the data
    const confirmedPlayers = signupsData ? signupsData.filter(s => s.signup_status === 'confirmed') : [];
    const waitlistPlayers = signupsData ? signupsData.filter(s => s.signup_status === 'waitlist') : [];
    const cancelledPlayers = signupsData ? signupsData.filter(s => s.signup_status === 'cancelled') : [];

    const session = {
      ...sessionData,
      session_signups: signupsData || [],
      confirmed_players: confirmedPlayers,
      waitlist_players: waitlistPlayers,
      cancelled_players: cancelledPlayers,
      current_players: confirmedPlayers.length,
      is_full: confirmedPlayers.length >= (sessionData.max_players || 16),
      can_signup: canSignupByCapacity(sessionData, confirmedPlayers.length),
      clubs: clubData,
      createdBy: creatorData,
      club_session_setup: setupData
    };

    return session;
  } catch (error) {
    console.error('Error fetching session details:', error);
    throw error;
  }
};

// Sign up a player for a session
export const signupForSession = async (sessionId, playerId) => {
  try {
    // Ensure both IDs are strings (not objects)
    const sessionIdString = typeof sessionId === 'object' ? sessionId.id || sessionId.toString() : sessionId;
    const playerIdString = typeof playerId === 'object' ? playerId.id || playerId.toString() : playerId;
    
    console.log('Signing up for session:', { 
      sessionId: sessionIdString, 
      playerId: playerIdString, 
      originalSessionId: sessionId, 
      originalPlayerId: playerId 
    });
    
    // First, check if player is already signed up
    const { data: existingSignup } = await supabase
      .from('session_signups')
      .select('*')
      .eq('session_id', sessionIdString)
      .eq('player_id', playerIdString)
      .maybeSingle(); // Use maybeSingle() to handle no results gracefully

    if (existingSignup) {
      console.log('Player already signed up:', existingSignup);
      
      // If they have a cancelled status, allow them to re-sign up
      if (existingSignup.signup_status === 'cancelled') {
        console.log('Player has cancelled status, allowing re-signup');
        // We'll update the existing record instead of creating a new one
      } else {
        throw new Error('Player is already signed up for this session');
      }
    }

    // Get basic session info 
    const { data: session, error: sessionError } = await supabase
      .from('sessions')
      .select('*')
      .eq('id', sessionIdString)
      .single();

    if (sessionError) throw sessionError;

    // Count current signups
    const { data: currentSignups, error: signupsError } = await supabase
      .from('session_signups')
      .select('id, signup_status, waitlist_position')
      .eq('session_id', sessionIdString);

    if (signupsError) throw signupsError;

    const confirmedCount = currentSignups.filter(s => s.signup_status === 'confirmed').length;
    const waitlistCount = currentSignups.filter(s => s.signup_status === 'waitlist').length;
    const placement = initialSignupPlacement(session, {
      confirmedCount,
      waitlistCount,
      maxPlayers: session.max_players || 16,
    });

    if (placement.blocked) {
      throw new Error(placement.blockedReason || 'Session is full and waitlist is disabled');
    }

    const waitlistPosition = placement.waitlistPosition;

    // Check signup deadline
    if (session.signup_deadline_hours) {
      const deadline = new Date(session.session_date);
      deadline.setHours(deadline.getHours() - session.signup_deadline_hours);
      
      if (new Date() > deadline) {
        throw new Error('Signup deadline has passed');
      }
    }

    // Create or update the signup
    let data, error;
    
    if (existingSignup && existingSignup.signup_status === 'cancelled') {
      // Update existing cancelled signup
      const result = await supabase
        .from('session_signups')
        .update({
          signup_status: placement.signupStatus,
          waitlist_position: waitlistPosition,
          signup_date: new Date().toISOString() // Update signup date to now
        })
        .eq('session_id', sessionIdString)
        .eq('player_id', playerIdString)
        .select()
        .single();
      
      data = result.data;
      error = result.error;
    } else {
      // Create new signup
      const result = await supabase
        .from('session_signups')
        .insert([{
          session_id: sessionIdString,
          player_id: playerIdString,
          signup_status: placement.signupStatus,
          waitlist_position: waitlistPosition,
          signup_date: new Date().toISOString()
        }])
        .select()
        .single();
      
      data = result.data;
      error = result.error;
    }

    if (error) throw error;

    return {
      ...data,
      is_waitlist: !!waitlistPosition,
      waitlist_position: waitlistPosition
    };
  } catch (error) {
    console.error('Error signing up for session:', error);
    throw error;
  }
};

/**
 * Host paste-roster write: insert or reactivate session_signups in one batch.
 * Skips players already confirmed or waitlisted. Cancelled rows are updated.
 *
 * @param {{ sessionId: string, addedBy?: string, assignments: Array<{ playerId: string, signupStatus: 'confirmed'|'waitlist', waitlistPosition?: number|null }> }} params
 */
export const bulkAddSessionSignups = async ({ sessionId, addedBy, assignments }) => {
  const rows = (assignments || []).filter((row) => row?.playerId && row?.signupStatus);
  if (!sessionId || !rows.length) {
    return { added: 0, confirmed: 0, waitlist: 0, already: 0 };
  }

  const playerIds = [...new Set(rows.map((row) => row.playerId))];
  const { data: existing, error: existingError } = await supabase
    .from('session_signups')
    .select('id, player_id, signup_status')
    .eq('session_id', sessionId)
    .in('player_id', playerIds);

  if (existingError) throw existingError;

  const existingByPlayerId = new Map((existing || []).map((row) => [row.player_id, row]));
  const now = new Date().toISOString();
  const toInsert = [];
  const toUpdate = [];
  let already = 0;
  let confirmed = 0;
  let waitlist = 0;

  rows.forEach((assignment) => {
    const current = existingByPlayerId.get(assignment.playerId);
    if (current && current.signup_status !== 'cancelled') {
      already += 1;
      return;
    }
    if (assignment.signupStatus === 'waitlist') waitlist += 1;
    else confirmed += 1;

    const payload = {
      signup_status: assignment.signupStatus,
      waitlist_position: assignment.signupStatus === 'waitlist' ? assignment.waitlistPosition : null,
      signup_date: now,
      added_by: addedBy || null,
    };

    if (current) {
      toUpdate.push({ id: current.id, ...payload });
    } else {
      toInsert.push({
        session_id: sessionId,
        player_id: assignment.playerId,
        ...payload,
      });
    }
  });

  if (toInsert.length) {
    const { error } = await supabase.from('session_signups').insert(toInsert);
    if (error) throw error;
  }

  if (toUpdate.length) {
    const results = await Promise.all(
      toUpdate.map((row) =>
        supabase
          .from('session_signups')
          .update({
            signup_status: row.signup_status,
            waitlist_position: row.waitlist_position,
            signup_date: row.signup_date,
            added_by: row.added_by,
          })
          .eq('id', row.id)
      )
    );
    const updateError = results.find((result) => result.error)?.error;
    if (updateError) throw updateError;
  }

  return {
    added: toInsert.length + toUpdate.length,
    confirmed,
    waitlist,
    already,
  };
};

// Cancel a player's signup
export const cancelSignup = async (sessionId, playerId) => {
  try {
    // Ensure both IDs are strings (not objects)
    const sessionIdString = typeof sessionId === 'object' ? sessionId.id || sessionId.toString() : sessionId;
    const playerIdString = typeof playerId === 'object' ? playerId.id || playerId.toString() : playerId;
    
    // First, get the current signup status to check if we need to promote a waitlist player
    const { data: currentSignup, error: fetchError } = await supabase
      .from('session_signups')
      .select('signup_status')
      .eq('session_id', sessionIdString)
      .eq('player_id', playerIdString)
      .single();

    if (fetchError) throw fetchError;

    // Update the signup status to 'cancelled' instead of deleting
    const { data, error } = await supabase
      .from('session_signups')
      .update({ 
        signup_status: 'cancelled'
      })
      .eq('session_id', sessionIdString)
      .eq('player_id', playerIdString)
      .select()
      .single();

    if (error) throw error;

    if (currentSignup.signup_status === 'confirmed') {
      const { data: sessionRow } = await supabase
        .from('sessions')
        .select('signup_selection')
        .eq('id', sessionIdString)
        .maybeSingle();
      if (!isHostSelectSignup(sessionRow)) {
        await supabase.rpc('promote_waitlist_player', { session_uuid: sessionIdString });
      }
    }

    return data;
  } catch (error) {
    console.error('Error canceling signup:', error);
    throw error;
  }
};

// Admin: Manually add a player to a session
export const manuallyAddPlayer = async (sessionId, playerId, addedBy) => {
  try {
    // Check if player is already signed up
    const { data: existingSignup } = await supabase
      .from('session_signups')
      .select('*')
      .eq('session_id', sessionId)
      .eq('player_id', playerId)
      .maybeSingle(); // Use maybeSingle() to handle no results gracefully

    if (existingSignup) {
      throw new Error('Player is already signed up for this session');
    }

    // Add player as confirmed (admin override)
    const { data, error } = await supabase
      .from('session_signups')
      .insert([{
        session_id: sessionId,
        player_id: playerId,
        signup_status: 'confirmed',
        waitlist_position: null,
        added_by: addedBy,
        signup_date: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error manually adding player:', error);
    throw error;
  }
};

// Admin: Update session details
export const updateSession = async (sessionId, updates) => {
  try {
    // Clean up the updates to ensure proper data types
    const cleanedUpdates = { ...updates };
    
    // Convert empty strings to null for integer fields
    if (cleanedUpdates.signup_deadline_hours === '') {
      cleanedUpdates.signup_deadline_hours = null;
    }
    if (cleanedUpdates.max_players === '') {
      cleanedUpdates.max_players = null;
    }
    
    // Convert string numbers to integers
    if (typeof cleanedUpdates.signup_deadline_hours === 'string' && cleanedUpdates.signup_deadline_hours) {
      cleanedUpdates.signup_deadline_hours = parseInt(cleanedUpdates.signup_deadline_hours);
    }
    if (typeof cleanedUpdates.max_players === 'string' && cleanedUpdates.max_players) {
      cleanedUpdates.max_players = parseInt(cleanedUpdates.max_players);
    }

    if (isHostSelectSignup(cleanedUpdates.signup_selection)) {
      cleanedUpdates.signup_selection = 'host_select';
    }
    if (cleanedUpdates.waitlist_enabled !== undefined) {
      cleanedUpdates.waitlist_enabled = true;
    }
    if (cleanedUpdates.session_series_id === '') {
      cleanedUpdates.session_series_id = null;
    }

    console.log('Updating session with cleaned data:', cleanedUpdates);

    const { data, error } = await supabase
      .from('sessions')
      .update(cleanedUpdates)
      .eq('id', sessionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating session:', error);
    throw error;
  }
};

// Admin: Cancel a session (marks as cancelled)
export const cancelSession = async (sessionId) => {
  try {
    // First, update all session_players to cancelled status
    const { error: playersError } = await supabase
      .from('session_players')
      .update({ 
        status: 'cancelled',
        exit_reason: 'cancelled'
      })
      .eq('session_id', sessionId);

    if (playersError) throw playersError;

    // Update all session_signups to cancelled status
    const { error: signupsError } = await supabase
      .from('session_signups')
      .update({ 
        signup_status: 'cancelled'
      })
      .eq('session_id', sessionId);

    if (signupsError) throw signupsError;

    // Then update the session status
    const { data, error } = await supabase
      .from('sessions')
      .update({ session_status: 'cancelled' })
      .eq('id', sessionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error cancelling session:', error);
    throw error;
  }
};

// Admin: Uncancel a session (marks as upcoming)
export const uncancelSession = async (sessionId) => {
  try {
    // First, reset session_players who were cancelled back to signed_up
    const { error: playersError } = await supabase
      .from('session_players')
      .update({ 
        status: 'signed_up',
        exit_reason: null
      })
      .eq('session_id', sessionId)
      .eq('status', 'cancelled');

    if (playersError) throw playersError;

    // Reset session_signups that were cancelled back to confirmed
    const { error: signupsError } = await supabase
      .from('session_signups')
      .update({ 
        signup_status: 'confirmed'
      })
      .eq('session_id', sessionId)
      .eq('signup_status', 'cancelled');

    if (signupsError) throw signupsError;

    // Then update the session status
    const { data, error } = await supabase
      .from('sessions')
      .update({ session_status: 'upcoming' })
      .eq('id', sessionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error uncancelling session:', error);
    throw error;
  }
};

// Update session status (ongoing, ended, closed)
export const updateSessionStatus = async (sessionId, newStatus) => {
  try {
    const { data, error } = await supabase.rpc('set_session_status', {
      p_session_id: sessionId,
      p_status: newStatus,
    });

    if (error) throw error;

    if (String(newStatus).toLowerCase() === 'completed') {
      triggerSessionCompletionEffects(sessionId);
    }

    return data;
  } catch (error) {
    console.error('Error updating session status:', error);
    throw error;
  }
};

/**
 * Full "start session" flow: set status to ongoing, add confirmed signups as session_players, init ELO (best-effort).
 * Mirrors Upcoming Sessions / Home host behaviour — do not use a shallow status-only update for this.
 *
 * @param {string} sessionId
 * @param {string} starterPlayerId - player who is starting (used for sessions.created_by)
 * @returns {Promise<{ session: object; alreadyOngoing: boolean }>}
 */
export const startScheduledClubSession = async (sessionId, starterPlayerId) => {
  const sessionDetails = await getSessionDetails(sessionId);

  if (sessionDetails.session_status === 'ongoing') {
    return { session: sessionDetails, alreadyOngoing: true };
  }

  const confirmedSignups = sessionDetails.confirmed_players || [];
  const playerIds = confirmedSignups.map((signup) => signup.player_id);

  let seasonId = sessionDetails.season_id;
  if (!seasonId) {
    const { data: currentSeason, error: currentSeasonError } = await supabase
      .from('seasons')
      .select('id')
      .eq('club_id', sessionDetails.club_id)
      .eq('season_status', 'current')
      .maybeSingle();

    if (currentSeasonError) {
      console.error('[startScheduledClubSession] Error fetching current season:', currentSeasonError);
      throw currentSeasonError;
    }

    if (currentSeason?.id) {
      seasonId = currentSeason.id;
    } else {
      const { data: recentSeason, error: recentError } = await supabase
        .from('seasons')
        .select('id')
        .eq('club_id', sessionDetails.club_id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (recentError) {
        console.error('[startScheduledClubSession] Error fetching recent season:', recentError);
        throw new Error('No season found for this club');
      }
      if (!recentSeason?.id) {
        throw new Error('No season found for this club');
      }
      seasonId = recentSeason.id;
    }
  }

  const { data: updatedSession, error: updateError } = await supabase
    .from('sessions')
    .update({
      session_status: 'ongoing',
      session_date: new Date().toISOString(),
      created_by: starterPlayerId,
      season_id: seasonId,
    })
    .eq('id', sessionId)
    .select()
    .single();

  if (updateError) {
    throw new Error(`Failed to start session: ${updateError.message}`);
  }
  if (!updatedSession) {
    throw new Error('Session update returned no data - check RLS policies and user permissions');
  }

  try {
    const { initializeEloRatingsForClub } = await import('./guestService');
    await initializeEloRatingsForClub(sessionDetails.club_id, seasonId);
  } catch (eloError) {
    console.warn('[startScheduledClubSession] ELO init skipped:', eloError);
  }

  try {
    const { addPlayersToSession } = await import('./sessionService');
    await addPlayersToSession(
      updatedSession.id,
      playerIds,
      sessionDetails.club_id,
      'signed_up'
    );
  } catch (addPlayersError) {
    throw new Error(`Failed to add players to session: ${addPlayersError.message}`);
  }

  return { session: updatedSession, alreadyOngoing: false };
};

// Admin: Delete a session (removes completely, including leaderboard snapshots from wrap-up)
export const deleteSession = async (sessionId) => {
  try {
    const { data, error } = await supabase.rpc('delete_session_with_dependencies', {
      p_session_id: sessionId,
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error deleting session:', error);
    throw error;
  }
};

// Check if a session has payment records
export const sessionHasPaymentRecords = async (sessionId) => {
  try {
    // Check for payment allocations via session_players
    const { data: sessionPlayers, error: spError } = await supabase
      .from('session_players')
      .select('id')
      .eq('session_id', sessionId)
      .limit(1);

    if (spError) throw spError;

    if (!sessionPlayers || sessionPlayers.length === 0) {
      return false;
    }

    const sessionPlayerIds = sessionPlayers.map(sp => sp.id);

    const { data: allocations, error: allocError } = await supabase
      .from('payment_allocations')
      .select(`
        id,
        payment_ledger:payment_id!inner(is_deleted)
      `)
      .in('session_player_id', sessionPlayerIds)
      .limit(1);

    if (allocError) throw allocError;

    // Check if any active (not deleted) payment allocations exist
    const hasActivePayments = (allocations || []).some(
      alloc => alloc.payment_ledger && !alloc.payment_ledger.is_deleted
    );

    return hasActivePayments;
  } catch (error) {
    console.error('Error checking payment records:', error);
    return false; // Fail safely - assume no payment records if check fails
  }
};

// Get session signup status for a player
export const getPlayerSignupStatus = async (sessionId, playerId) => {
  try {
    // Ensure both IDs are strings (not objects)
    const sessionIdString = typeof sessionId === 'object' ? sessionId.id || sessionId.toString() : sessionId;
    const playerIdString = typeof playerId === 'object' ? playerId.id || playerId.toString() : playerId;
    
    const { data, error } = await supabase
      .from('session_signups')
      .select('id, signup_status, waitlist_position, signup_date')
      .eq('session_id', sessionIdString)
      .eq('player_id', playerIdString)
      .maybeSingle(); // Use maybeSingle() instead of single() to handle no results gracefully

    if (error) {
      console.error('Error fetching signup status:', error);
      throw error;
    }

    if (!data) {
      return { signup_status: 'not_signed_up', waitlist_position: null };
    }

    return {
      signup_status: data.signup_status,
      waitlist_position: data.waitlist_position,
      signup_date: data.signup_date
    };
  } catch (error) {
    console.error('Error getting player signup status:', error);
    // Return default status instead of throwing to prevent UI errors
    return { signup_status: 'not_signed_up', waitlist_position: null };
  }
};

/** True when the player is on the live roster (not only a pre-start signup). */
export const isPlayerOnLiveRoster = async (sessionId, playerId) => {
  try {
    const sessionIdString = typeof sessionId === 'object' ? sessionId.id || sessionId.toString() : sessionId;
    const playerIdString = typeof playerId === 'object' ? playerId.id || playerId.toString() : playerId;
    const { data, error } = await supabase
      .from('session_players')
      .select('status')
      .eq('session_id', sessionIdString)
      .eq('player_id', playerIdString)
      .in('status', ['active', 'signed_up', 'paused'])
      .maybeSingle();

    if (error) {
      console.error('Error checking live roster status:', error);
      return false;
    }
    return Boolean(data);
  } catch (error) {
    console.error('Error checking live roster status:', error);
    return false;
  }
};

// Get session templates for a club
export const getSessionTemplates = async (clubId) => {
  try {
    const { data, error } = await supabase
      .from('club_session_setup')
      .select(`
        *,
        session_types(name, description)
      `)
      .eq('club_id', clubId)
      .eq('is_active', true)
      .order('session_name');

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching session templates:', error);
    throw error;
  }
};

// Create a new session template
export const createTemplate = async (templateData) => {
  try {
    const { data, error } = await supabase
      .from('club_session_setup')
      .insert([{
        club_id: templateData.club_id,
        created_by: templateData.created_by,
        session_name: templateData.session_name,
        description: templateData.description,
        ideal_player_count: templateData.ideal_player_count,
        number_of_courts: templateData.number_of_courts,
        waitlist_enabled: true,
        waitlist_promotion_type: templateData.waitlist_promotion_type,
        signup_selection: isHostSelectSignup(templateData.signup_selection) ? 'host_select' : 'first_come',
        signup_deadline_hours: templateData.signup_deadline_hours,
        is_public: templateData.is_public,
        price_per_player: templateData.price_per_player,
        court_booking_cost: templateData.court_booking_cost,
        location: templateData.location,
        min_elo_level: templateData.min_elo_level,
        start_time: templateData.start_time,
        end_time: templateData.end_time,
        currency_code: templateData.currency_code,
        session_type_id: templateData.session_type_id,
        game_type_restriction: templateData.game_type_restriction || 'all_types',
        enable_player_categorisation: templateData.enable_player_categorisation || false,
        competitive_match_format: templateData.competitive_match_format || 'doubles',
        points_target: templateData.points_target ?? 21,
        deuce_mode: templateData.deuce_mode || 'standard',
        peg_board_selection_window: templateData.peg_board_selection_window ?? 6,
        peg_board_queue_return: normalizePegBoardQueueReturn(templateData.peg_board_queue_return),
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating session template:', error);
    throw error;
  }
};
